import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import AppShell from '@/components/AppShell';
import { CreditCard, Check, Loader2, AlertTriangle, Sparkles, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

function SubscriptionManagement() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, organization, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const isNorwegian = i18n.language === 'nb' || i18n.language === 'no';
  
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [usageStats, setUsageStats] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [packages, setPackages] = useState({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedBillingPeriod, setSelectedBillingPeriod] = useState('monthly');
  const [stripeStatus, setStripeStatus] = useState(null);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);

  // Check for returning from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [searchParams]);

  // Poll payment status
  const checkPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Betalingsstatus tidsavbrutt. Sjekk e-post for bekreftelse.');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.get(
        `${API}/stripe/checkout/${sessionId}/status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.payment_status === 'paid') {
        toast.success('✅ Betaling vellykket! Abonnementet er aktivert.');
        setCheckingPayment(false);
        // Remove session_id from URL
        window.history.replaceState({}, '', '/subscription');
        fetchData();
        return;
      } else if (response.data.status === 'expired') {
        toast.error('Betalingen utløp. Vennligst prøv igjen.');
        setCheckingPayment(false);
        return;
      }

      // Continue polling
      toast.loading('Behandler betaling...', { id: 'payment-poll' });
      setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      toast.error('Feil ved sjekking av betalingsstatus');
      setCheckingPayment(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('systemklart_token');
      
      const [subRes, usageRes, tiersRes, stripeRes, packagesRes] = await Promise.all([
        axios.get(`${API}/subscriptions/me`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/subscriptions/me/usage`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: null })),
        axios.get(`${API}/subscriptions/tiers`),
        axios.get(`${API}/stripe/status`).catch(() => ({ data: { configured: false } })),
        axios.get(`${API}/stripe/packages`).catch(() => ({ data: { packages: {} } }))
      ]);
      
      setSubscription(subRes.data);
      setUsageStats(usageRes.data);
      setTiers(tiersRes.data.tiers);
      setStripeStatus(stripeRes.data);
      setPackages(packagesRes.data.packages || {});
    } catch (error) {
      console.error('Failed to fetch subscription data:', error);
      toast.error('Kunne ikke laste abonnementsdata');
    } finally {
      setLoading(false);
    }
  };

  const getPackageId = (tierId, billingPeriod) => {
    return `${tierId}_${billingPeriod}`;
  };

  const handleStripePayment = async () => {
    if (!selectedTier) return;
    
    const tier = tiers.find(t => t.id === selectedTier);
    
    // Enterprise = contact sales
    if (tier?.is_contact_sales) {
      toast.success('Vi kontakter deg snart! Send gjerne e-post til kontakt@systemklar.no');
      setShowUpgradeModal(false);
      return;
    }
    
    // Check if yearly is available
    const billingPeriod = tier?.has_yearly_option && selectedBillingPeriod === 'yearly' 
      ? 'yearly' 
      : 'monthly';
    
    const packageId = getPackageId(selectedTier, billingPeriod);
    
    // Verify package exists
    if (!packages[packageId]) {
      toast.error('Ugyldig pakke valgt');
      return;
    }
    
    setProcessingPayment(true);
    const toastId = toast.loading('Starter Stripe-betaling...');
    
    try {
      const token = localStorage.getItem('systemklart_token');
      const response = await axios.post(
        `${API}/stripe/checkout`,
        { 
          package_id: packageId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Omdirigerer til Stripe...', { id: toastId });
      
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Stripe payment failed:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke starte betaling', { id: toastId });
      setProcessingPayment(false);
    }
  };

  const handleUpgradeWithoutPayment = async () => {
    // For when Stripe is not configured - manual/invoice flow
    if (!selectedTier) return;
    
    const toastId = toast.loading('Oppgraderer abonnement...');
    try {
      const token = localStorage.getItem('systemklart_token');
      
      if (subscription) {
        await axios.patch(
          `${API}/subscriptions/${subscription.id}/upgrade`,
          { 
            new_tier: selectedTier,
            new_billing_period: selectedBillingPeriod
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${API}/subscriptions`,
          { 
            tier: selectedTier,
            billing_period: selectedBillingPeriod
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      toast.success('✅ Abonnement oppdatert! (Faktura sendes separat)', { id: toastId });
      setShowUpgradeModal(false);
      fetchData();
    } catch (error) {
      console.error('Failed to upgrade:', error);
      toast.error('❌ Kunne ikke oppdatere abonnement', { id: toastId });
    }
  };

  const handleCancel = async () => {
    if (!subscription) return;
    
    const confirmed = window.confirm('Er du sikker på at du vil kansellere abonnementet?');
    if (!confirmed) return;
    
    const toastId = toast.loading('Kansellerer abonnement...');
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.delete(
        `${API}/subscriptions/${subscription.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Abonnement kansellert', { id: toastId });
      fetchData();
    } catch (error) {
      console.error('Failed to cancel:', error);
      toast.error('❌ Kunne ikke kansellere abonnement', { id: toastId });
    }
  };

  if (loading || checkingPayment) {
    return (
      <AppShell>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-12 h-12 text-cyan-500 animate-spin mx-auto" />
            <p className="mt-4 text-slate-400">
              {checkingPayment 
                ? (isNorwegian ? 'Bekrefter betaling...' : 'Confirming payment...') 
                : (isNorwegian ? 'Laster abonnementsinfo...' : 'Loading subscription info...')}
            </p>
          </div>
        </div>
      </AppShell>
    );
  }

  const currentTier = subscription ? tiers.find(t => t.id === subscription.tier) : null;
  const usagePercent = usageStats && usageStats.ai_generations_limit > 0 
    ? Math.round((usageStats.ai_generations_used / usageStats.ai_generations_limit) * 100) 
    : 0;

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <CreditCard className="w-8 h-8 text-cyan-500" />
            <h1 className="text-2xl font-bold text-white">
              {isNorwegian ? 'Abonnement' : 'Subscription'}
            </h1>
          </div>
          <p className="text-slate-400">
            {isNorwegian ? 'Administrer ditt abonnement' : 'Manage your subscription'}
          </p>
        </div>

        {/* Current Plan */}
        <div className="bg-slate-900 rounded-lg shadow-lg p-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-4">
            {isNorwegian ? 'Ditt Abonnement' : 'Your Subscription'}
          </h2>
          
          {subscription ? (
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-semibold text-white">{currentTier?.name || subscription.tier}</h3>
                      {currentTier?.is_recommended && (
                        <span className="bg-emerald-500/20 text-emerald-400 text-xs px-2 py-1 rounded-full">
                          {isNorwegian ? 'Anbefalt' : 'Recommended'}
                        </span>
                      )}
                    </div>
                    <p className="text-slate-400">
                      {subscription.billing_period === 'monthly' 
                        ? (isNorwegian ? 'Månedlig' : 'Monthly') 
                        : (isNorwegian ? 'Årlig' : 'Yearly')} {isNorwegian ? 'fakturering' : 'billing'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-3xl font-bold text-cyan-400">
                      kr {subscription.billing_period === 'monthly' ? currentTier?.price_monthly : currentTier?.price_yearly}
                    </p>
                    <p className="text-sm text-slate-500">
                      {isNorwegian ? 'per' : 'per'} {subscription.billing_period === 'monthly' ? (isNorwegian ? 'måned' : 'month') : (isNorwegian ? 'år' : 'year')}
                    </p>
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <p><strong>{isNorwegian ? 'Status' : 'Status'}:</strong> <span className={`px-2 py-1 rounded ${subscription.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400'}`}>{subscription.status === 'active' ? (isNorwegian ? 'Aktiv' : 'Active') : subscription.status}</span></p>
                  <p><strong>{isNorwegian ? 'Startdato' : 'Start date'}:</strong> {new Date(subscription.start_date).toLocaleDateString(isNorwegian ? 'no-NO' : 'en-US')}</p>
                  {subscription.end_date && (
                    <p><strong>{isNorwegian ? 'Fornyes' : 'Renews'}:</strong> {new Date(subscription.end_date).toLocaleDateString(isNorwegian ? 'no-NO' : 'en-US')}</p>
                  )}
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="px-4 py-2 bg-cyan-500 text-white rounded-md hover:bg-cyan-600 font-medium"
                  >
                    {isNorwegian ? 'Endre abonnement' : 'Change subscription'}
                  </button>
                  {subscription.status === 'active' && (
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 border border-rose-500/30 text-rose-400 rounded-md hover:bg-rose-500/10 font-medium"
                    >
                      {isNorwegian ? 'Kanseller' : 'Cancel'}
                    </button>
                  )}
                </div>
              </div>

              {/* Usage Stats */}
              <div className="border-l border-slate-700 pl-6">
                <h4 className="font-semibold text-white mb-4">
                  {isNorwegian ? 'Forbruk denne måneden' : 'Usage this month'}
                </h4>
                
                {usageStats && (
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-slate-400">{isNorwegian ? 'AI-genereringer' : 'AI generations'}</span>
                        <span className="text-sm font-medium text-slate-300">
                          {usageStats.ai_generations_used} / {usageStats.ai_generations_limit === 0 ? (isNorwegian ? '0 (oppgrader for AI)' : '0 (upgrade for AI)') : usageStats.ai_generations_limit}
                        </span>
                      </div>
                      {usageStats.ai_generations_limit > 0 && (
                        <div className="w-full bg-slate-700 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${usagePercent >= 90 ? 'bg-rose-500' : usagePercent >= 70 ? 'bg-amber-500' : 'bg-cyan-500'}`}
                            style={{ width: `${Math.min(usagePercent, 100)}%` }}
                          ></div>
                        </div>
                      )}
                    </div>

                    <div>
                      <div className="flex justify-between mb-1">
                        <span className="text-sm text-slate-400">{isNorwegian ? 'Brukere' : 'Users'}</span>
                        <span className="text-sm font-medium text-slate-300">
                          {usageStats.users_count} / {usageStats.users_limit}
                        </span>
                      </div>
                      <div className="w-full bg-slate-700 rounded-full h-2">
                        <div 
                          className="bg-cyan-500 h-2 rounded-full"
                          style={{ width: `${Math.min((usageStats.users_count / usageStats.users_limit) * 100, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="mt-6 space-y-2">
                      <h5 className="font-semibold text-white text-sm mb-2">
                        {isNorwegian ? 'Tilgjengelige funksjoner:' : 'Available features:'}
                      </h5>
                      <FeatureItem enabled={usageStats.can_generate_ai} text={isNorwegian ? 'AI-generering' : 'AI generation'} />
                      <FeatureItem enabled={usageStats.can_export_pdf} text={isNorwegian ? 'PDF-eksport' : 'PDF export'} />
                      <FeatureItem enabled={usageStats.can_export_word} text={isNorwegian ? 'Word-eksport' : 'Word export'} />
                      <FeatureItem enabled={usageStats.can_use_special_modules} text={isNorwegian ? 'Spesialmoduler (SHA/HACCP/Legionella)' : 'Special modules (SHA/HACCP/Legionella)'} />
                      <FeatureItem enabled={usageStats.can_use_certification_modules} text={isNorwegian ? 'Sertifiseringsmoduler (ISO/Miljøfyrtårn)' : 'Certification modules (ISO/Eco-Lighthouse)'} />
                      <FeatureItem enabled={usageStats.can_edit_risks} text={isNorwegian ? 'Redigering av risikoer' : 'Risk editing'} />
                      <FeatureItem enabled={usageStats.has_versioning} text={isNorwegian ? 'Versjonering' : 'Versioning'} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-400 mb-4">
                {isNorwegian ? 'Du har ikke et aktivt abonnement' : 'You don\'t have an active subscription'}
              </p>
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="px-6 py-3 bg-cyan-500 text-white rounded-md hover:bg-cyan-600 font-medium"
              >
                {isNorwegian ? 'Velg abonnement' : 'Choose subscription'}
              </button>
            </div>
          )}
        </div>

        {/* Available Tiers */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">
            {isNorwegian ? 'Våre abonnementer' : 'Our subscriptions'}
          </h2>
          <p className="text-slate-400 mb-6">
            {isNorwegian ? 'Velg det nivået som passer best for din virksomhet' : 'Choose the level that best suits your business'}
          </p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {tiers.map(tier => (
              <TierCard 
                key={tier.id} 
                tier={tier} 
                isCurrentTier={subscription?.tier === tier.id}
                onSelect={() => {
                  setSelectedTier(tier.id);
                  setShowUpgradeModal(true);
                }}
              />
            ))}
          </div>
        </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-4">Velg abonnement</h3>
            
            <div className="space-y-3 mb-6">
              {tiers.map(tier => {
                const isSelected = selectedTier === tier.id;
                const showYearly = tier.has_yearly_option && selectedBillingPeriod === 'yearly';
                const displayPrice = showYearly ? tier.price_yearly : tier.price_monthly;
                
                return (
                  <button
                    key={tier.id}
                    onClick={() => setSelectedTier(tier.id)}
                    className={`w-full text-left p-4 border-2 rounded-lg transition-all ${
                      isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-700 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-white">{tier.name}</h4>
                          {tier.is_recommended && (
                            <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">Anbefalt</span>
                          )}
                          {tier.is_contact_sales && (
                            <span className="bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">Kontakt oss</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-400 mt-1">{tier.description}</p>
                        <div className="mt-2 text-xs text-slate-500">
                          {tier.limits.max_ai_generations_per_month === 0 
                            ? 'Ingen AI' 
                            : tier.limits.max_ai_generations_per_month >= 999 
                              ? 'Ubegrenset AI' 
                              : `${tier.limits.max_ai_generations_per_month} AI-gen/mnd`
                          }
                          {' • '}
                          {tier.limits.max_users >= 999 ? 'Ubegrenset brukere' : `${tier.limits.max_users} brukere`}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        {tier.is_contact_sales ? (
                          <>
                            <p className="text-lg font-bold text-white">Fra kr {tier.price_monthly}</p>
                            <p className="text-xs text-slate-500">/mnd</p>
                          </>
                        ) : (
                          <>
                            <p className="text-lg font-bold text-cyan-400">kr {displayPrice}</p>
                            <p className="text-xs text-slate-500">
                              /{showYearly ? 'år' : 'mnd'}
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Billing period selection */}
            {selectedTier && tiers.find(t => t.id === selectedTier)?.has_yearly_option && (
              <div className="mb-6 p-4 bg-slate-800 rounded-lg">
                <p className="text-sm font-medium text-slate-300 mb-2">Faktureringssyklus:</p>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="billing"
                      value="monthly"
                      checked={selectedBillingPeriod === 'monthly'}
                      onChange={(e) => setSelectedBillingPeriod(e.target.value)}
                      className="mr-2"
                    />
                    <span>Månedlig</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="billing"
                      value="yearly"
                      checked={selectedBillingPeriod === 'yearly'}
                      onChange={(e) => setSelectedBillingPeriod(e.target.value)}
                      className="mr-2"
                    />
                    <span>Årlig <span className="text-green-600 text-sm">(spar 15%)</span></span>
                  </label>
                </div>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="flex-1 px-4 py-2 border border-slate-700 text-slate-300 rounded-md hover:bg-slate-800"
              >
                Avbryt
              </button>
              
              {stripeStatus?.configured && selectedTier && !tiers.find(t => t.id === selectedTier)?.is_contact_sales ? (
                <button
                  onClick={handleStripePayment}
                  disabled={!selectedTier || processingPayment}
                  className="flex-1 px-4 py-2 bg-[#635BFF] text-white rounded-md hover:bg-[#5147e6] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" viewBox="0 0 60 25" fill="currentColor">
                    <path d="M59.64 14.28c0-4.8-2.32-8.58-6.76-8.58-4.48 0-7.16 3.78-7.16 8.54 0 5.64 3.19 8.5 7.76 8.5 2.23 0 3.92-.51 5.19-1.22v-3.76c-1.27.65-2.73 1.04-4.59 1.04-1.82 0-3.43-.64-3.63-2.85h9.14c0-.24.05-1.2.05-1.67zm-9.24-1.78c0-2.12 1.3-3 2.48-3 1.16 0 2.37.88 2.37 3h-4.85zm-12.15-6.8c-1.83 0-3.01.86-3.67 1.46l-.24-1.16h-4.13V24l4.68-.99v-5.25c.67.48 1.66 1.17 3.3 1.17 3.34 0 6.38-2.68 6.38-8.58-.02-5.4-3.1-8.35-6.32-8.35zm-1.11 12.85c-1.1 0-1.75-.4-2.2-.89V11.4c.48-.54 1.14-.92 2.2-.92 1.68 0 2.85 1.89 2.85 4.04 0 2.19-1.15 4.03-2.85 4.03zM19.64 5.7l4.7-.99V1L19.64 2v3.7zm0 1.22h4.7v15.93h-4.7V6.92zM14.55 8.12l-.3-1.2h-4.05v15.93h4.68v-10.8c1.1-1.44 2.97-1.17 3.55-.97V6.92c-.6-.22-2.78-.63-3.88 1.2zM4.96 2.92L.35 3.87l-.02 14.58c0 2.69 2.02 4.68 4.71 4.68 1.49 0 2.58-.28 3.18-.6v-3.82c-.58.23-3.45 1.06-3.45-1.6V10.6h3.45V6.92h-3.45l.19-4z"/>
                  </svg>
                  Betal med Stripe
                </button>
              ) : (
                <button
                  onClick={tiers.find(t => t.id === selectedTier)?.is_contact_sales 
                    ? () => { toast.success('Vi kontakter deg snart!'); setShowUpgradeModal(false); }
                    : handleUpgradeWithoutPayment
                  }
                  disabled={!selectedTier}
                  className="flex-1 px-4 py-2 bg-cyan-500 text-white rounded-md hover:bg-cyan-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {tiers.find(t => t.id === selectedTier)?.is_contact_sales 
                    ? 'Kontakt oss' 
                    : 'Velg abonnement'
                  }
                </button>
              )}
            </div>
            
            {!stripeStatus?.configured && (
              <p className="text-xs text-slate-500 mt-4 text-center">
                {isNorwegian ? 'Kortbetaling kommer snart. Abonnementet aktiveres med faktura.' : 'Card payment coming soon. Subscription activated via invoice.'}
              </p>
            )}
          </div>
        </div>
      )}
      </div>
    </AppShell>
  );
}

function FeatureItem({ enabled, text }) {
  return (
    <div className="flex items-center">
      {enabled ? (
        <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      ) : (
        <svg className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      )}
      <span className={`text-sm ${enabled ? 'text-slate-300' : 'text-gray-400'}`}>{text}</span>
    </div>
  );
}

function TierCard({ tier, isCurrentTier, onSelect }) {
  return (
    <div 
      className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all hover:shadow-lg ${
        isCurrentTier 
          ? 'border-cyan-500 bg-cyan-500/10' 
          : tier.is_recommended 
            ? 'border-emerald-400 bg-emerald-500/10' 
            : 'border-slate-700 bg-slate-800/50 hover:border-cyan-500/50'
      }`}
      onClick={onSelect}
    >
      {isCurrentTier && (
        <span className="absolute -top-3 left-4 bg-cyan-500 text-white text-xs px-2 py-1 rounded-full">
          Ditt abonnement
        </span>
      )}
      {tier.is_recommended && !isCurrentTier && (
        <span className="absolute -top-3 left-4 bg-green-500 text-white text-xs px-2 py-1 rounded-full">
          Anbefalt
        </span>
      )}
      
      <h3 className="text-lg font-bold text-white mt-1">{tier.name}</h3>
      <p className="text-xs text-slate-500 mb-3 line-clamp-2">{tier.description}</p>
      
      <p className="text-2xl font-bold text-cyan-400">
        {tier.is_contact_sales ? 'Fra ' : ''}kr {tier.price_monthly}
        <span className="text-sm text-slate-500 font-normal">/mnd</span>
      </p>
      
      {tier.price_yearly && (
        <p className="text-xs text-slate-400">eller kr {tier.price_yearly}/år (spar 15%)</p>
      )}
      {!tier.has_yearly_option && !tier.is_contact_sales && (
        <p className="text-xs text-slate-500">Kun månedlig</p>
      )}
      
      <ul className="mt-4 space-y-1 text-xs">
        <li className="text-slate-300">
          {tier.limits.max_users >= 999 ? '∞' : tier.limits.max_users} bruker{tier.limits.max_users !== 1 ? 'e' : ''}
        </li>
        <li className="text-slate-300">
          {tier.limits.max_ai_generations_per_month === 0 
            ? 'Ingen AI' 
            : tier.limits.max_ai_generations_per_month >= 999 
              ? '∞ AI-gen' 
              : `${tier.limits.max_ai_generations_per_month} AI-gen/mnd`
          }
        </li>
        <li className={tier.limits.has_export ? 'text-slate-300' : 'text-slate-500'}>
          {tier.limits.has_export ? '✓' : '✗'} Eksport
        </li>
        <li className={tier.limits.has_special_modules ? 'text-slate-300' : 'text-slate-500'}>
          {tier.limits.has_special_modules ? '✓' : '✗'} Spesialmoduler
        </li>
        <li className={tier.limits.has_certification_modules ? 'text-slate-300' : 'text-slate-500'}>
          {tier.limits.has_certification_modules ? '✓' : '✗'} Sertifisering
        </li>
      </ul>
      
      <button className="w-full mt-4 px-3 py-2 text-sm bg-cyan-500 text-white rounded-md hover:bg-cyan-600">
        {isCurrentTier ? 'Administrer' : tier.is_contact_sales ? 'Kontakt oss' : 'Velg'}
      </button>
    </div>
  );
}

export default SubscriptionManagement;
